import { useState } from 'react';
import { useTheme } from '../../styles/ThemeContext';
import { Field } from '../../components/forms/Field';

// Fields that must be filled before proceeding to step 2
const REQUIRED = new Set([
  'collCompany', 'collAddress', 'collCity', 'collCountry', 'collContact',
  'delCompany', 'delAddress', 'delCity', 'delCountry', 'delContact',
  'preparedBy'
]);

/**
 * Step 1: Collection & Delivery Addresses, Project Info
 */
export function Step1Addresses({
  sd,
  onUpdate,
  countries,
  savedAddresses,
  selectedAddrId,
  onSelectAddress,
  onSaveAddress,
  onClearAddress,
  onDeleteAddress,
  onNext,
  externalError,
  onClearExternalError,
  user
}) {
  const [showErrors, setShowErrors] = useState(false);
  const { theme } = useTheme();
  const c = theme.colors;

  // Combine internal and external error triggers
  const errorsVisible = showErrors || externalError;

  const upd = (field, value) => {
    onUpdate(field, value);
    if (errorsVisible && REQUIRED.has(field) && value.trim()) {
      setShowErrors(false);
      if (onClearExternalError) onClearExternalError();
    }
  };

  const missingFields = [...REQUIRED].filter((f) => !sd[f]?.trim());

  const handleNext = () => {
    if (missingFields.length > 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    if (onClearExternalError) onClearExternalError();
    onNext();
  };

  const isErr = (field) => errorsVisible && REQUIRED.has(field) && !sd[field]?.trim();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={theme.sectTag}>STEP 01</span>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Address Details</h2>
      </div>

      {/* Project Info */}
      <div style={theme.card}>
        <div
          style={{
            fontSize: 9,
            color: c.accent,
            letterSpacing: 1,
            marginBottom: 12,
            fontFamily: 'monospace'
          }}
        >
          PROJECT INFO ──────────────────
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field l="Department Number">
            <input
              value={sd.deptNumber}
              onChange={(e) => upd('deptNumber', e.target.value)}
              placeholder="e.g. 2024-NL-001"
              style={theme.inp}
            />
          </Field>
          <Field l="Project #">
            <input
              value={sd.projectNumber}
              onChange={(e) => upd('projectNumber', e.target.value)}
              placeholder="e.g. P-2024-0142"
              style={theme.inp}
            />
          </Field>
          <Field l="Prepared By *">
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={sd.preparedBy}
                onChange={(e) => upd('preparedBy', e.target.value)}
                placeholder="Your name"
                style={{
                  ...theme.inp,
                  flex: 1,
                  borderColor: isErr('preparedBy') ? c.error : undefined
                }}
              />
              {user?.username && (
                <button
                  type="button"
                  onClick={() => upd('preparedBy', user.username)}
                  title={`Fill in: ${user.username}`}
                  style={{
                    background: c.accentBg,
                    border: '1px solid ' + c.accentBorder,
                    color: c.accent,
                    padding: '0 10px',
                    borderRadius: 3,
                    cursor: 'pointer',
                    fontSize: 10,
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap'
                  }}
                >
                  👤 Me
                </button>
              )}
            </div>
          </Field>
        </div>
      </div>

      {/* Collection and Delivery Addresses */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {[
          [
            '📦 COLLECTION ADDRESS',
            'coll',
            [
              ['collCompany', 'Company *'],
              ['collAddress', 'Address *'],
              ['collPostal', 'Postal Code'],
              ['collCity', 'City *'],
              ['collCountry', 'Country *'],
              ['collContact', 'Contact Person *'],
              ['collPhone', 'Tel #'],
              ['collEmail', 'E-mail']
            ]
          ],
          [
            '🚚 DELIVERY ADDRESS',
            'del',
            [
              ['delCompany', 'Company *'],
              ['delAddress', 'Address *'],
              ['delPostal', 'Postal Code'],
              ['delCity', 'City *'],
              ['delCountry', 'Country *'],
              ['delContact', 'Contact Person *'],
              ['delPhone', 'Tel #'],
              ['delEmail', 'E-mail']
            ]
          ]
        ].map(([title, prefix, fields]) => (
          <div key={title} style={theme.card}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: c.accent,
                  letterSpacing: 1,
                  fontFamily: 'monospace'
                }}
              >
                {title} ──────────────
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {selectedAddrId?.[prefix] && (
                  <button
                    onClick={() => onClearAddress(prefix)}
                    title="Clear all fields and start a new address"
                    style={{
                      background: 'transparent',
                      border: '1px solid ' + c.border,
                      color: c.textSecondary,
                      padding: '3px 8px',
                      borderRadius: 3,
                      cursor: 'pointer',
                      fontSize: 10,
                      fontFamily: 'monospace'
                    }}
                  >
                    ✚ New
                  </button>
                )}
                <button
                  onClick={() => onSaveAddress(prefix)}
                  title={selectedAddrId?.[prefix] ? "Update this saved address" : "Save this address for later use"}
                  style={{
                    background: selectedAddrId?.[prefix] ? c.accentBg : 'transparent',
                    border: '1px solid ' + (selectedAddrId?.[prefix] ? c.accentBorder : c.successBorder),
                    color: selectedAddrId?.[prefix] ? c.accent : c.success,
                    padding: '3px 8px',
                    borderRadius: 3,
                    cursor: 'pointer',
                    fontSize: 10,
                    fontFamily: 'monospace'
                  }}
                >
                  {selectedAddrId?.[prefix] ? '💾 Update' : '💾 Save'}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  fontSize: 10,
                  color: c.textSecondary,
                  display: 'block',
                  marginBottom: 4,
                  fontFamily: 'monospace',
                  letterSpacing: 0.5
                }}
              >
                Load saved address
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  onChange={(e) => {
                    if (e.target.value) onSelectAddress(e.target.value, prefix);
                    e.target.value = '';
                  }}
                  style={{ ...theme.sel, flex: 1 }}
                >
                  <option value="">-- Select saved address --</option>
                  {savedAddresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.company}
                      {a.city ? ' — ' + a.city : ''}
                      {a.country ? ' (' + a.country + ')' : ''}
                    </option>
                  ))}
                </select>
                {savedAddresses.length > 0 && (
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        onDeleteAddress(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    style={{
                      ...theme.sel,
                      width: 44,
                      padding: '7px 4px',
                      color: c.error,
                      textAlign: 'center'
                    }}
                    title="Delete a saved address"
                  >
                    <option value="">🗑</option>
                    {savedAddresses.map((a) => (
                      <option key={a.id} value={a.id}>
                        × {a.company}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {fields.map(([f, l]) =>
                f.includes('Country') ? (
                  <Field key={f} l={l}>
                    <select
                      value={sd[f]}
                      onChange={(e) => upd(f, e.target.value)}
                      style={{
                        ...theme.sel,
                        borderColor: isErr(f) ? c.error : undefined
                      }}
                    >
                      <option value="">-- Select --</option>
                      {countries.map((ct) => (
                        <option key={ct.code} value={ct.code}>
                          {ct.name} ({ct.code})
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : (
                  <Field key={f} l={l}>
                    <input
                      value={sd[f]}
                      onChange={(e) => upd(f, e.target.value)}
                      style={{
                        ...theme.inp,
                        borderColor: isErr(f) ? c.error : undefined
                      }}
                    />
                  </Field>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Shipment Details */}
      <div style={theme.card}>
        <div
          style={{
            fontSize: 9,
            color: c.accent,
            letterSpacing: 1,
            marginBottom: 12,
            fontFamily: 'monospace'
          }}
        >
          SHIPMENT DETAILS ──────────────
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field l="Pick-up Date">
            <input
              type="date"
              value={sd.pickupDate}
              onChange={(e) => upd('pickupDate', e.target.value)}
              style={theme.inp}
            />
          </Field>
          <Field l="Delivery Deadline">
            <input
              type="date"
              value={sd.deliveryDate}
              onChange={(e) => upd('deliveryDate', e.target.value)}
              style={theme.inp}
            />
          </Field>
        </div>
      </div>

      {/* Validation error */}
      {errorsVisible && missingFields.length > 0 && (
        <div
          style={{
            background: c.errorBg,
            border: '1px solid ' + c.errorBorder,
            borderRadius: 4,
            padding: '10px 14px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span style={{ fontSize: 14 }}>⚠️</span>
          <span style={{ fontSize: 12, color: c.error, fontFamily: 'monospace' }}>
            Not all required fields (*) are filled in. Please complete the highlighted fields to continue.
          </span>
        </div>
      )}

      {/* Navigation */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: 16,
          borderTop: '1px solid ' + c.border
        }}
      >
        <button onClick={handleNext} style={theme.btnP}>
          Next: Shipping Units →
        </button>
      </div>
    </div>
  );
}
